/**
 * Trade Mint Strategy — faithful port of MT4 MT4_Trade_Mint.mq4
 *
 * Uses EMA(30) + first-M5-candle breakout system:
 *   - Entry when Close breaks above H15M (first M5 candle High) + close > EMA(30) → BUY
 *   - Entry when Close breaks below L15M (first M5 candle Low) + close < EMA(30) → SELL
 *   - Requires pullback after exit before re-entry
 *
 * Target/SL: ATR(14) based
 *   - Target = Entry ± ATR × 2.0
 *   - StopLoss = Entry ∓ ATR × 1.5
 *
 * Since we receive raw candle data (no separate M5 feed), we approximate:
 *   - First candle = first bar of each trading day
 *   - H15M = first candle High, L15M = first candle Low
 *   - Trading starts from the 3rd candle of the day onward
 *
 * MT4 features ported:
 *   - EMA(30) filter for signal confirmation
 *   - First-candle breakout levels
 *   - ATR-based target/SL
 *   - Buy/Sell arrow markers
 *   - Target/SL hit detection (Target checked first on same bar)
 *   - Booked P&L and Max Points tracking
 *   - Custom colored candles: Green (#008000), Red (#FF0000), Gray (#808080)
 *   - Dashboard metadata for overlay component
 *
 * Category: Strategies | Pane: Overlay
 */
import { IndicatorDefinition, PlotData, IndicatorMarker, IndicatorPriceLine } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { inferTickSize, roundToTick } from "./price-utils";

// ─── Constants ───────────────────────────────────────────────────
const DIR_NONE = 0;
const DIR_LONG = 1;
const DIR_SHORT = -1;
type Direction = typeof DIR_NONE | typeof DIR_LONG | typeof DIR_SHORT;

// ─── ATR computation (Wilder smoothing) ──────────────────────────
function computeATR(candles: CandleData[], period: number): number[] {
  const atr: number[] = new Array(candles.length).fill(0);
  if (candles.length < 2) return atr;
  const tr: number[] = [candles[0].high - candles[0].low];
  for (let i = 1; i < candles.length; i++) {
    const h = candles[i].high, l = candles[i].low, pc = candles[i - 1].close;
    tr.push(Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)));
  }
  let sum = 0;
  for (let i = 0; i < Math.min(period, tr.length); i++) sum += tr[i];
  atr[period - 1] = sum / period;
  for (let i = period; i < tr.length; i++) {
    atr[i] = (atr[i - 1] * (period - 1) + tr[i]) / period;
  }
  return atr;
}

// ─── EMA computation ─────────────────────────────────────────────
function computeEMA(candles: CandleData[], period: number): number[] {
  const ema: number[] = new Array(candles.length).fill(0);
  if (candles.length === 0) return ema;
  const k = 2.0 / (period + 1);
  // SMA seed
  let sum = 0;
  for (let i = 0; i < Math.min(period, candles.length); i++) sum += candles[i].close;
  ema[Math.min(period - 1, candles.length - 1)] = sum / Math.min(period, candles.length);
  // EMA forward
  for (let i = period; i < candles.length; i++) {
    ema[i] = candles[i].close * k + ema[i - 1] * (1 - k);
  }
  return ema;
}

// ─── IST day boundary detection (UTC+5:30) ──────────────────────
const IST_OFFSET_SEC = 5 * 3600 + 30 * 60;

function getISTDate(unixSec: number): number {
  // Returns a unique integer per IST calendar day: YYYYMMDD
  const d = new Date((unixSec + IST_OFFSET_SEC) * 1000);
  return d.getUTCFullYear() * 10000 + (d.getUTCMonth() + 1) * 100 + d.getUTCDate();
}

// ─── Dashboard data interface ────────────────────────────────────
export interface TradeMintDashboard {
  signal: "BUY" | "SELL" | null;
  entry: number | null;
  target: number | null;
  stopLoss: number | null;
  currentPrice: number;
  status: "Waiting" | "Buy Active" | "Sell Active" | "Target Hit" | "Stop Loss Hit";
  bookedPL: number;
  maxPoints: number;
  trend: "Up Trend" | "Down Trend" | "Sideways";
  candleState: "long" | "short" | "neutral";
}

export const tradeMintIndicator: IndicatorDefinition = {
  id: "trade_mint",
  name: "Trade Mint",
  shortName: "TMT",
  category: "strategies",
  pane: "overlay",
  params: [
    { key: "alertEnabled", label: "Alert Notification", type: "boolean", default: true },
    { key: "atrPeriod", label: "ATR Period", type: "number", default: 14 },
    { key: "targetMultiplier", label: "Target ATR Mult", type: "number", default: 2.0 },
    { key: "slMultiplier", label: "SL ATR Mult", type: "number", default: 1.5 },
    { key: "emaPeriod", label: "EMA Period", type: "number", default: 30 },
    { key: "displayLevels", label: "Display Lines", type: "select", default: "yes", options: [
      { label: "Yes", value: "yes" }, { label: "No", value: "no" },
    ]},
    { key: "lineWidth", label: "Line Width", type: "number", default: 1, min: 1, max: 4, step: 1 },
    { key: "showBackground", label: "Candle Color", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
  ],
  plots: [
    { key: "entry", label: "Entry", color: "#00FF00", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "target", label: "Target", color: "#00FF00", style: "line", lineWidth: 2, lineStyle: 0, lastValueVisible: false },
    { key: "stopLoss", label: "Stop Loss", color: "#FF0000", style: "line", lineWidth: 2, lineStyle: 0, lastValueVisible: false },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const atrPeriod = params.atrPeriod ?? 14;
    const targetMult = params.targetMultiplier ?? 2.0;
    const slMult = params.slMultiplier ?? 1.5;
    const emaPeriod = params.emaPeriod ?? 30;
    const displayLevels = params.displayLevels ?? "yes";
    const showBackground = params.showBackground ?? "yes";
    const len = candles.length;

    const emptyResult: PlotData[] = [
      { key: "entry", data: [] }, { key: "target", data: [] }, { key: "stopLoss", data: [] },
    ];

    if (len < emaPeriod + 5) return emptyResult;

    const tick = inferTickSize(candles);
    const lw = Math.max(1, Math.min(4, Math.round(params.lineWidth ?? 1)));

    // ════════════════════════════════════════════════════════════
    // 1. Compute EMA(30) and ATR(14), EMA(200) for trend
    // ════════════════════════════════════════════════════════════
    const ema = computeEMA(candles, emaPeriod);
    const atr = computeATR(candles, atrPeriod);
    const ema200 = computeEMA(candles, 200);

    // ════════════════════════════════════════════════════════════
    // 2. Identify first candle of each day → H15M/L15M
    // ════════════════════════════════════════════════════════════
    const firstCandleHigh: number[] = new Array(len).fill(0);
    const firstCandleLow: number[] = new Array(len).fill(0);
    const tradingAllowed: boolean[] = new Array(len).fill(false);

    let currentDayStart = -1;
    let dayH = 0, dayL = 0;
    let candlesInDay = 0;

    for (let i = 0; i < len; i++) {
      const ds = getISTDate(candles[i].time);
      if (ds !== currentDayStart) {
        currentDayStart = ds;
        dayH = candles[i].high;
        dayL = candles[i].low;
        candlesInDay = 1;
      } else {
        candlesInDay++;
      }
      firstCandleHigh[i] = dayH;
      firstCandleLow[i] = dayL;
      tradingAllowed[i] = candlesInDay >= 3; // 3rd candle onward
    }

    // ════════════════════════════════════════════════════════════
    // 3. Signal Generation
    // ════════════════════════════════════════════════════════════
    const markers: IndicatorMarker[] = [];
    const arrowMarkers: { time: number; direction: "long" | "short"; price: number }[] = [];
    const SIGNAL: Direction[] = new Array(len).fill(DIR_NONE);
    const ENTRY: number[] = new Array(len).fill(0);
    const TARGET: number[] = new Array(len).fill(0);
    const STOPLOSS: number[] = new Array(len).fill(0);
    const candleColorState: ("long" | "short" | "neutral")[] = new Array(len).fill("neutral");

    let inBuyTrade = false;
    let inSellTrade = false;
    let previousTradeCompleted = true;
    let lastSignalBar = -1;
    let lastExitBar = -1;
    let entryPriceActive = 0;
    let targetPriceActive = 0;
    let slPriceActive = 0;
    let maxHighAfterBuy = 0;
    let minLowAfterSell = Infinity;
    let bookedPL = 0;
    let maxPoints = 0;
    let lastStatus: TradeMintDashboard["status"] = "Waiting";
    let lastDayStart = -1;

    const lastClosedBar = len - 2;

    for (let i = emaPeriod; i <= lastClosedBar; i++) {
      const close = candles[i].close;
      const h15m = firstCandleHigh[i];
      const l15m = firstCandleLow[i];
      const emaVal = ema[i];

      // Daily reset
      const ds = getISTDate(candles[i].time);
      if (ds !== lastDayStart) {
        lastDayStart = ds;
        inBuyTrade = false;
        inSellTrade = false;
        previousTradeCompleted = true;
        entryPriceActive = 0;
        targetPriceActive = 0;
        slPriceActive = 0;
        maxHighAfterBuy = 0;
        minLowAfterSell = Infinity;
        bookedPL = 0;
        maxPoints = 0;
        lastStatus = "Waiting";
      }

      if (!tradingAllowed[i]) {
        SIGNAL[i] = DIR_NONE;
        continue;
      }

      // Check exit for active trades
      if (inBuyTrade && entryPriceActive > 0) {
        maxHighAfterBuy = Math.max(maxHighAfterBuy, candles[i].high);
        maxPoints = maxHighAfterBuy - entryPriceActive;
        // Target hit first (priority)
        if (candles[i].high >= targetPriceActive) {
          bookedPL = targetPriceActive - entryPriceActive;
          lastStatus = "Target Hit";
          inBuyTrade = false;
          previousTradeCompleted = true;
          lastExitBar = i;
          markers.push({ time: candles[i].time, position: "aboveBar", shape: "circle", color: "#00FF00", text: "✓ TP", size: 1 });
        } else if (candles[i].low <= slPriceActive) {
          bookedPL = slPriceActive - entryPriceActive;
          lastStatus = "Stop Loss Hit";
          inBuyTrade = false;
          previousTradeCompleted = true;
          lastExitBar = i;
          markers.push({ time: candles[i].time, position: "belowBar", shape: "circle", color: "#FFA500", text: "✗ SL", size: 1 });
        }
      } else if (inSellTrade && entryPriceActive > 0) {
        minLowAfterSell = Math.min(minLowAfterSell, candles[i].low);
        maxPoints = entryPriceActive - minLowAfterSell;
        if (candles[i].low <= targetPriceActive) {
          bookedPL = entryPriceActive - targetPriceActive;
          lastStatus = "Target Hit";
          inSellTrade = false;
          previousTradeCompleted = true;
          lastExitBar = i;
          markers.push({ time: candles[i].time, position: "belowBar", shape: "circle", color: "#00FF00", text: "✓ TP", size: 1 });
        } else if (candles[i].high >= slPriceActive) {
          bookedPL = entryPriceActive - slPriceActive;
          lastStatus = "Stop Loss Hit";
          inSellTrade = false;
          previousTradeCompleted = true;
          lastExitBar = i;
          markers.push({ time: candles[i].time, position: "aboveBar", shape: "circle", color: "#FFA500", text: "✗ SL", size: 1 });
        }
      }

      // Check for new signals
      if (!inBuyTrade && previousTradeCompleted && close > h15m && close > emaVal) {
        // Retracement check: after last exit, must have closed below H15M
        let retracementOK = lastExitBar < 0;
        if (!retracementOK) {
          for (let j = lastExitBar + 1; j < i; j++) {
            if (candles[j].close < h15m) { retracementOK = true; break; }
          }
        }
        if (retracementOK || lastExitBar < 0) {
          // BUY signal
          const atrVal = atr[i] > 0 ? atr[i] : 1;
          entryPriceActive = roundToTick(close, tick);
          targetPriceActive = roundToTick(close + atrVal * targetMult, tick);
          slPriceActive = roundToTick(close - atrVal * slMult, tick);
          inBuyTrade = true;
          inSellTrade = false;
          previousTradeCompleted = false;
          maxHighAfterBuy = candles[i].high;
          lastSignalBar = i;
          bookedPL = 0;
          maxPoints = 0;
          lastStatus = "Buy Active";

          SIGNAL[i] = DIR_LONG;
          ENTRY[i] = entryPriceActive;
          TARGET[i] = targetPriceActive;
          STOPLOSS[i] = slPriceActive;

          markers.push({
            time: candles[i].time, position: "belowBar",
            color: "#008000", shape: "arrowUp", text: "", size: 2,
          });
          arrowMarkers.push({ time: candles[i].time, direction: "long" as const, price: candles[i].low });
        }
      } else if (!inSellTrade && previousTradeCompleted && close < l15m && close < emaVal) {
        let retracementOK = lastExitBar < 0;
        if (!retracementOK) {
          for (let j = lastExitBar + 1; j < i; j++) {
            if (candles[j].close > l15m) { retracementOK = true; break; }
          }
        }
        if (retracementOK || lastExitBar < 0) {
          const atrVal = atr[i] > 0 ? atr[i] : 1;
          entryPriceActive = roundToTick(close, tick);
          targetPriceActive = roundToTick(close - atrVal * targetMult, tick);
          slPriceActive = roundToTick(close + atrVal * slMult, tick);
          inBuyTrade = false;
          inSellTrade = true;
          previousTradeCompleted = false;
          minLowAfterSell = candles[i].low;
          lastSignalBar = i;
          bookedPL = 0;
          maxPoints = 0;
          lastStatus = "Sell Active";

          SIGNAL[i] = DIR_SHORT;
          ENTRY[i] = entryPriceActive;
          TARGET[i] = targetPriceActive;
          STOPLOSS[i] = slPriceActive;

          markers.push({
            time: candles[i].time, position: "aboveBar",
            color: "#FF0000", shape: "arrowDown", text: "", size: 2,
          });
          arrowMarkers.push({ time: candles[i].time, direction: "short" as const, price: candles[i].high });
        }
      }

      // Forward fill levels
      if (SIGNAL[i] === DIR_NONE) {
        SIGNAL[i] = SIGNAL[i - 1] || DIR_NONE;
        ENTRY[i] = ENTRY[i - 1] || 0;
        TARGET[i] = TARGET[i - 1] || 0;
        STOPLOSS[i] = STOPLOSS[i - 1] || 0;
      }

      // Candle color state
      if (inBuyTrade) candleColorState[i] = "long";
      else if (inSellTrade) candleColorState[i] = "short";
      else candleColorState[i] = "neutral";
    }

    // Forward-fill to forming bar
    if (lastClosedBar >= 0 && lastClosedBar < len - 1) {
      for (let i = lastClosedBar + 1; i < len; i++) {
        SIGNAL[i] = SIGNAL[lastClosedBar]; ENTRY[i] = ENTRY[lastClosedBar];
        TARGET[i] = TARGET[lastClosedBar]; STOPLOSS[i] = STOPLOSS[lastClosedBar];
        candleColorState[i] = candleColorState[lastClosedBar];
      }
    }

    // ── Live bar hit detection ────────────────────────────────────────────────
    // The main loop only processes closed bars (up to lastClosedBar = len-2).
    // Check the live (forming) bar so the dashboard status updates in real-time
    // as soon as price touches Target or SL, not just on candle close.
    if (len >= 2) {
      const liveBar = len - 1;
      if (inBuyTrade && entryPriceActive > 0 && lastStatus === "Buy Active") {
        if (candles[liveBar].high >= targetPriceActive) {
          lastStatus = "Target Hit";
          bookedPL = targetPriceActive - entryPriceActive;
        } else if (candles[liveBar].low <= slPriceActive) {
          lastStatus = "Stop Loss Hit";
          bookedPL = slPriceActive - entryPriceActive;
        }
      } else if (inSellTrade && entryPriceActive > 0 && lastStatus === "Sell Active") {
        if (candles[liveBar].low <= targetPriceActive) {
          lastStatus = "Target Hit";
          bookedPL = entryPriceActive - targetPriceActive;
        } else if (candles[liveBar].high >= slPriceActive) {
          lastStatus = "Stop Loss Hit";
          bookedPL = entryPriceActive - slPriceActive;
        }
      }
    }

    // ════════════════════════════════════════════════════════════
    // 4. Dashboard metadata
    // ════════════════════════════════════════════════════════════
    const uiBar = Math.max(0, len - 2);
    const currentPrice = candles[len - 1]?.close ?? 0;

    let dashSignal: TradeMintDashboard["signal"] = null;
    let livePL = 0;
    if (inBuyTrade && entryPriceActive > 0) {
      dashSignal = "BUY";
      livePL = currentPrice - entryPriceActive;
    } else if (inSellTrade && entryPriceActive > 0) {
      dashSignal = "SELL";
      livePL = entryPriceActive - currentPrice;
    }

    // Trend via EMA(200)
    let dashTrend: TradeMintDashboard["trend"] = "Sideways";
    if (len > 201) {
      if (candles[uiBar].close > ema200[uiBar]) dashTrend = "Up Trend";
      else if (candles[uiBar].close < ema200[uiBar]) dashTrend = "Down Trend";
    }

    const dashboard: TradeMintDashboard = {
      signal: dashSignal,
      entry: entryPriceActive > 0 ? entryPriceActive : null,
      target: targetPriceActive > 0 ? targetPriceActive : null,
      stopLoss: slPriceActive > 0 ? slPriceActive : null,
      currentPrice,
      status: lastStatus,
      bookedPL,
      maxPoints: Math.max(maxPoints, 0),
      trend: dashTrend,
      candleState: candleColorState[uiBar] || "neutral",
    };

    // ════════════════════════════════════════════════════════════
    // 5. Build plot data + candle colors
    // ════════════════════════════════════════════════════════════
    const entryData: { time: number; value: number }[] = [];
    const tgtData: { time: number; value: number }[] = [];
    const slData: { time: number; value: number }[] = [];

    // Plot series intentionally left empty — price lines below handle all
    // level drawing (start from entry candle, extend to right edge with axis badges).

    const candleColors: { time: number; color: string }[] = [];
    if (showBackground !== "no") for (let i = 0; i < len; i++) {
      const state = candleColorState[i];
      if (state === "long") candleColors.push({ time: candles[i].time, color: "#008000" });
      else if (state === "short") candleColors.push({ time: candles[i].time, color: "#FF0000" });
      else candleColors.push({ time: candles[i].time, color: "#808080" });
    }

    // ════════════════════════════════════════════════════════════
    // 6. Price lines — extend from entry candle to right edge + axis badges
    // ════════════════════════════════════════════════════════════
    const priceLines: IndicatorPriceLine[] = [];
    // Use ENTRY/TARGET/STOPLOSS arrays (not the live variables) so that price lines
    // survive across day boundaries — the daily reset zeroes entryPriceActive but
    // never touches these per-bar arrays, so the last trade's levels remain visible
    // even when today has no new signal yet (e.g. 10m chart where last trade was yesterday).
    const displayEntry = lastSignalBar >= 0 ? ENTRY[lastSignalBar] : 0;
    const displayTarget = lastSignalBar >= 0 ? TARGET[lastSignalBar] : 0;
    const displaySL = lastSignalBar >= 0 ? STOPLOSS[lastSignalBar] : 0;
    if (displayLevels !== "no" && lastSignalBar >= 0 && displayEntry > 0) {
      const entryTime = candles[lastSignalBar].time;
      const isBuySignal = SIGNAL[lastSignalBar] === DIR_LONG;
      const entryColor = isBuySignal ? "#00FF00" : "#FF0000";
      priceLines.push(
        { id: "tmt-entry",  price: displayEntry,  color: entryColor,  title: "Entry",     lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
        { id: "tmt-target", price: displayTarget, color: "#00FF00",   title: "Target",    lineVisible: true, lineWidth: lw, lineStyle: 0, axisLabelVisible: true, fromTime: entryTime },
        { id: "tmt-sl",     price: displaySL,     color: "#FF0000",   title: "Stop Loss", lineVisible: true, lineWidth: lw, lineStyle: 0, axisLabelVisible: true, fromTime: entryTime },
      );
    }

    return [
      { key: "entry", data: entryData, markers, priceLines, metadata: { dashboard, candleColors, arrowMarkers } },
      { key: "target", data: tgtData },
      { key: "stopLoss", data: slData },
    ];
  },
};
